/**
 * Cart Drawer
 *
 * Slide-out cart that intercepts:
 *  - Clicks on any link/button pointing to /cart
 *  - Submits of any form posting to /cart/add
 *
 * Re-renders itself by fetching the cart-drawer section via the
 * Shopify Section Rendering API (`?sections=cart-drawer`).
 *
 * Dispatches a `cart:updated` event with the latest cart object after every change.
 */
(function () {
  'use strict';

  var SECTION_ID = 'cart-drawer';
  var SELECTORS = {
    drawer: '[data-cart-drawer]',
    panel: '[data-cart-panel]',
    overlay: '[data-cart-overlay]',
    close: '[data-cart-close]',
    closeOnClick: '[data-cart-close-on-click]',
    qtyBtn: '[data-cart-qty]',
    remove: '[data-cart-remove]',
    cartLink: 'a[href$="/cart"], a[href*="/cart?"], a[href="#cart"]',
    addForm: 'form[action$="/cart/add"], form[action*="/cart/add"]',
    cartCount: '[data-cart-count]',
    cartIcon: '[data-cart-icon]'
  };

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var CartDrawer = {
    drawer: null,
    isOpen: false,
    lastFocus: null,

    init: function () {
      this.drawer = document.querySelector(SELECTORS.drawer);
      if (!this.drawer) return;

      this.bindGlobalEvents();
      this.bindDrawerEvents();
      this.updateShipDate();
      this.handleOpenFlag();
    },

    handleOpenFlag: function () {
      // Open drawer if a referring page or /cart redirect set ?cart=open.
      try {
        var url = new URL(window.location.href);
        if (url.searchParams.get('cart') === 'open') {
          url.searchParams.delete('cart');
          var clean = url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '') + url.hash;
          window.history.replaceState({}, '', clean);
          var self = this;
          // Defer slightly so page has rendered before slide-in animation runs.
          window.setTimeout(function () { self.open(); }, 50);
        }
      } catch (e) { /* noop */ }
    },

    bindGlobalEvents: function () {
      var self = this;

      // Intercept all /cart links
      document.addEventListener('click', function (e) {
        var link = e.target.closest(SELECTORS.cartLink);
        if (!link) return;
        // Allow modifier-key clicks (open in new tab) to pass through
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        self.open();
      });

      // Intercept add-to-cart forms
      document.addEventListener('submit', function (e) {
        var form = e.target.closest(SELECTORS.addForm);
        if (!form) return;
        e.preventDefault();
        self.handleAddToCart(form);
      });

      // ESC closes
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.isOpen) {
          e.preventDefault();
          self.close();
        }
      });
    },

    bindDrawerEvents: function () {
      var self = this;

      this.drawer.addEventListener('click', function (e) {
        if (e.target.closest(SELECTORS.overlay)) {
          self.close();
          return;
        }
        if (e.target.closest(SELECTORS.close)) {
          self.close();
          return;
        }
        if (e.target.closest(SELECTORS.closeOnClick)) {
          // Let the link navigate, but close the drawer
          self.close();
          return;
        }

        var qtyBtn = e.target.closest(SELECTORS.qtyBtn);
        if (qtyBtn) {
          e.preventDefault();
          self.handleQuantityChange(qtyBtn);
          return;
        }

        var removeBtn = e.target.closest(SELECTORS.remove);
        if (removeBtn) {
          e.preventDefault();
          self.handleRemove(removeBtn);
          return;
        }
      });

      // Focus trap
      this.drawer.addEventListener('keydown', function (e) {
        if (e.key !== 'Tab' || !self.isOpen) return;
        var focusable = Array.from(self.drawer.querySelectorAll(FOCUSABLE)).filter(function (el) {
          return el.offsetParent !== null;
        });
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      });
    },

    open: function () {
      if (this.isOpen) return;
      this.isOpen = true;
      this.lastFocus = document.activeElement;
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-drawer-open');

      var self = this;
      // Focus the close button after the panel slides in
      window.setTimeout(function () {
        var closeBtn = self.drawer.querySelector(SELECTORS.close);
        if (closeBtn) closeBtn.focus();
      }, 100);
    },

    close: function () {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-drawer-open');

      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }
    },

    setLoading: function (loading) {
      this.drawer.classList.toggle('is-loading', !!loading);
    },

    handleAddToCart: function (form) {
      var self = this;
      var submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.setAttribute('disabled', '');

      var formData = new FormData(form);
      // Ensure JSON response
      formData.set('sections', SECTION_ID);

      fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/javascript' },
        body: formData
      })
        .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
        .then(function (result) {
          if (!result.ok) {
            self.showError(result.data && result.data.description ? result.data.description : 'Could not add to cart.');
            return;
          }
          // Refresh drawer + cart object
          return self.refresh().then(function () { self.open(); });
        })
        .catch(function () {
          self.showError('Could not add to cart. Please try again.');
        })
        .finally(function () {
          if (submitBtn) submitBtn.removeAttribute('disabled');
        });
    },

    handleQuantityChange: function (btn) {
      var key = btn.getAttribute('data-line-key');
      var direction = btn.getAttribute('data-cart-qty');
      var item = btn.closest('[data-cart-item]');
      var valueEl = item ? item.querySelector('[data-cart-qty-value]') : null;
      var current = valueEl ? parseInt(valueEl.textContent, 10) : 0;
      var next = direction === 'increase' ? current + 1 : Math.max(0, current - 1);
      this.updateLine(key, next);
    },

    handleRemove: function (btn) {
      var key = btn.getAttribute('data-line-key');
      this.updateLine(key, 0);
    },

    updateLine: function (key, quantity) {
      var self = this;
      this.setLoading(true);

      fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: key, quantity: quantity, sections: SECTION_ID })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('cart/change failed');
          return res.json();
        })
        .then(function (data) {
          self.renderSection(data);
          self.updateHeaderCount(data);
          self.dispatch(data);
        })
        .catch(function () {
          self.showError('Could not update cart. Please try again.');
        })
        .finally(function () {
          self.setLoading(false);
        });
    },

    refresh: function () {
      var self = this;
      return fetch('/?sections=' + SECTION_ID, { headers: { 'Accept': 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          self.renderSection(data);
        })
        .then(function () {
          // Pull a fresh cart object for header count + listeners
          return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
            .then(function (res) { return res.json(); })
            .then(function (cart) {
              self.updateHeaderCount(cart);
              self.dispatch(cart);
            });
        });
    },

    renderSection: function (data) {
      if (!data) return;
      // /cart/change.js + /cart/add.js return { sections: { 'cart-drawer': '...' } }
      // /?sections=cart-drawer returns { 'cart-drawer': '...' }
      var html = (data.sections && data.sections[SECTION_ID]) || data[SECTION_ID];
      if (!html) return;

      var parser = new DOMParser();
      var doc = parser.parseFromString(html, 'text/html');
      var newDrawer = doc.querySelector(SELECTORS.drawer);
      if (!newDrawer) return;

      // Replace inner content (panel) but keep outer aside element + open state
      var newPanel = newDrawer.querySelector(SELECTORS.panel);
      var currentPanel = this.drawer.querySelector(SELECTORS.panel);
      if (newPanel && currentPanel) {
        currentPanel.innerHTML = newPanel.innerHTML;
      }

      // Ships-by line was just re-rendered — recompute date.
      this.updateShipDate();
    },

    /**
     * Ship-date estimate.
     *
     * Rule: orders placed before 3:00 PM Eastern on a US business day ship
     * the same day. Otherwise they ship on the next business day, skipping
     * weekends and observed US federal holidays.
     */
    updateShipDate: function () {
      var el = this.drawer.querySelector('[data-ship-date]');
      if (!el) return;
      var labelEl = el.querySelector('[data-ship-date-label]') || el;

      var todayStr = el.getAttribute('data-ships-today') || 'Ships by <b>today</b>';
      var template = el.getAttribute('data-ships-by') || 'Ships by <b>[date]</b>';

      try {
        var nyParts = nyNow();
        var cursor = utcDate(nyParts.year, nyParts.month - 1, nyParts.day);
        var todayKey = ymd(cursor);
        var holidaysCache = {};

        function isShippable(date) {
          var year = date.getUTCFullYear();
          if (!holidaysCache[year]) holidaysCache[year] = holidaysFor(year);
          var dow = date.getUTCDay();
          if (dow === 0 || dow === 6) return false;
          if (holidaysCache[year].has(ymd(date))) return false;
          return true;
        }

        var shipDate;
        if (isShippable(cursor) && nyParts.hour < 15) {
          shipDate = cursor;
        } else {
          do {
            cursor.setUTCDate(cursor.getUTCDate() + 1);
          } while (!isShippable(cursor));
          shipDate = cursor;
        }

        // Locale strings may contain <b>/<strong> markup, so use innerHTML.
        // The dynamic date portion is HTML-escaped to keep this XSS-safe.
        if (ymd(shipDate) === todayKey) {
          labelEl.innerHTML = todayStr;
        } else {
          labelEl.innerHTML = template.replace('[date]', escapeHtml(formatShipDate(shipDate)));
        }
      } catch (e) {
        el.hidden = true;
      }
    },

    updateHeaderCount: function (cart) {
      var count = (cart && typeof cart.item_count !== 'undefined') ? cart.item_count : 0;
      document.querySelectorAll(SELECTORS.cartCount).forEach(function (el) {
        el.textContent = count;
        el.toggleAttribute('hidden', count === 0);
      });
      document.querySelectorAll(SELECTORS.cartIcon).forEach(function (el) {
        el.toggleAttribute('data-empty', count === 0);
      });
    },

    dispatch: function (cart) {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    },

    showError: function (message) {
      // Simple inline announcement; merchant can style later if desired.
      // eslint-disable-next-line no-console
      console.warn('[cart-drawer]', message);
    }
  };

  // ─────────────────────────────────────────────────────
  // Ship-date helpers (US Eastern Time, federal holidays)
  // ─────────────────────────────────────────────────────

  function nyNow() {
    var fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: 'numeric', hour12: false
    });
    var parts = {};
    fmt.formatToParts(new Date()).forEach(function (p) {
      if (p.type !== 'literal') parts[p.type] = parseInt(p.value, 10);
    });
    if (parts.hour === 24) parts.hour = 0; // some impls report 24 at midnight
    return parts;
  }

  function utcDate(y, m, d) { return new Date(Date.UTC(y, m, d)); }
  function pad(n) { return n < 10 ? '0' + n : '' + n; }
  function ymd(d) {
    return d.getUTCFullYear() + '-' + pad(d.getUTCMonth() + 1) + '-' + pad(d.getUTCDate());
  }

  // nth weekday of a month, e.g. 3rd Monday in January
  // weekday: 0=Sun..6=Sat ; month: 0-indexed
  function nthWeekday(year, month, weekday, n) {
    var d = utcDate(year, month, 1);
    var offset = (weekday - d.getUTCDay() + 7) % 7;
    d.setUTCDate(1 + offset + (n - 1) * 7);
    return d;
  }

  // Last weekday of a month, e.g. last Monday in May
  function lastWeekday(year, month, weekday) {
    var d = utcDate(year, month + 1, 0); // last day of `month`
    var offset = (d.getUTCDay() - weekday + 7) % 7;
    d.setUTCDate(d.getUTCDate() - offset);
    return d;
  }

  // Returns Set of 'YYYY-MM-DD' strings for observed US federal holidays
  // in the given year. Fixed-date holidays (Jan 1, Jun 19, Jul 4, Nov 11,
  // Dec 25) shift to Friday/Monday when they fall on a weekend.
  function holidaysFor(year) {
    var s = new Set();

    function addObserved(month, day) {
      var d = utcDate(year, month, day);
      var dow = d.getUTCDay();
      if (dow === 6) d.setUTCDate(d.getUTCDate() - 1);       // Sat → Fri
      else if (dow === 0) d.setUTCDate(d.getUTCDate() + 1);  // Sun → Mon
      s.add(ymd(d));
    }
    function addExact(d) { s.add(ymd(d)); }

    addObserved(0, 1);                          // New Year's Day
    addExact(nthWeekday(year, 0, 1, 3));        // MLK Day (3rd Mon Jan)
    addExact(nthWeekday(year, 1, 1, 3));        // Presidents Day (3rd Mon Feb)
    addExact(lastWeekday(year, 4, 1));          // Memorial Day (last Mon May)
    addObserved(5, 19);                         // Juneteenth
    addObserved(6, 4);                          // Independence Day
    addExact(nthWeekday(year, 8, 1, 1));        // Labor Day (1st Mon Sep)
    addExact(nthWeekday(year, 9, 1, 2));        // Columbus Day (2nd Mon Oct)
    addObserved(10, 11);                        // Veterans Day
    addExact(nthWeekday(year, 10, 4, 4));       // Thanksgiving (4th Thu Nov)
    addObserved(11, 25);                        // Christmas

    return s;
  }

  // "Wednesday, Jan 9th" — short month, ordinal day
  function formatShipDate(d) {
    var weekday = new Intl.DateTimeFormat('en-US', { weekday: 'long', timeZone: 'UTC' }).format(d);
    var month = new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' }).format(d);
    var day = d.getUTCDate();
    return weekday + ', ' + month + ' ' + day + ordinalSuffix(day);
  }

  function ordinalSuffix(n) {
    var s = ['th', 'st', 'nd', 'rd'];
    var v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { CartDrawer.init(); });
  } else {
    CartDrawer.init();
  }

  // Expose for other scripts
  window.AVLCartDrawer = CartDrawer;
})();
